import { ComponentType, lazy } from 'react';

const RELOAD_TS_KEY = 'agapae:chunkReloadTs';
// If a reload was already triggered within this window and the chunk still
// fails, treat the deploy as genuinely broken and stop reloading, so a bad/
// partial deploy can't trap the user in an endless reload loop. A normal
// redeploy recovers on the first reload, well inside this window.
const RELOAD_WINDOW_MS = 10_000;

// Belt-and-suspenders against multiple chunks failing within a single page
// load (e.g. several lazy routes at once): only the first failure reloads.
let reloadScheduled = false;

/**
 * Detects the family of errors browsers throw when a content-hashed chunk no
 * longer exists after a redeploy and the SPA fallback serves index.html in its
 * place. Each engine phrases it differently, so match them all:
 * - Chrome/Edge: "Failed to fetch dynamically imported module"; strict variant
 *   "Failed to load module script: Expected a JavaScript module script but the
 *   server responded with a MIME type of text/html"
 * - Firefox: "error loading dynamically imported module"
 * - Safari/iOS: "'text/html' is not a valid JavaScript MIME type" /
 *   "Importing a module script failed"
 */
const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Failed to load module script/i.test(message) ||
    /is not a valid JavaScript MIME type/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
};

/**
 * Forces a single full-page reload to fetch the fresh index.html (and the new
 * chunk hashes) after a stale-chunk failure. Guarded by both an in-memory flag
 * (one reload per page load) and a sessionStorage timestamp (no immediate
 * re-reload after the reload), so a genuinely broken deploy can't loop.
 *
 * Returns true if a reload was scheduled — callers can use this to keep their
 * UI pending instead of surfacing the error.
 */
export const reloadForChunkError = (): boolean => {
  if (reloadScheduled) return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be unavailable (some private-mode contexts); the
    // in-memory guard above still prevents a loop within this page load.
  }
  reloadScheduled = true;
  window.location.reload();
  return true;
};

/**
 * Wraps React.lazy so a failed dynamic import (typically a stale SPA tab
 * pointing at chunk filenames that no longer exist after a deploy) triggers a
 * single full-page reload to fetch the fresh index.html.
 *
 * Why: Vite emits content-hashed chunks; old hashes 404 after redeploy and the
 * SPA fallback returns index.html, which fails the browser's module MIME check.
 * The global `vite:preloadError` handler in main.tsx is the primary, browser-
 * agnostic safety net (covers every dynamic import); this stays as a secondary
 * guard for the React.lazy path.
 * How to apply: use everywhere we use React.lazy for route-level code splitting.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && reloadForChunkError()) {
        // Reload scheduled — keep Suspense pending so we don't flash the error
        // boundary in the brief moment before the page tears down.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
